import { zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core';
import { adjacencyGraphs, dictionary } from '@zxcvbn-ts/language-common';
import { useMemo } from 'react';

/**
 * Visual password-strength feedback driven by zxcvbn-ts. Renders 4
 * horizontal segments that fill in left-to-right as the score climbs,
 * plus a one-word label below.
 *
 * The score is the same 0–4 scale zxcvbn uses everywhere. The page
 * gates submission on its own rules (length, match, TOTP) — this
 * component is informational, not authoritative.
 */

// One-time options bootstrap. zxcvbn's dictionary load is cheap once
// the bundle is parsed, so we do it at module scope rather than on
// every render. Same approach the API uses (apps/api/src/lib/auth/password.ts).
zxcvbnOptions.setOptions({
  dictionary: { ...dictionary },
  graphs: adjacencyGraphs,
});

type Score = 0 | 1 | 2 | 3 | 4;

type Props = {
  password: string;
};

const SEGMENT_FILL: Record<Score, [string, string, string, string]> = {
  // [seg0, seg1, seg2, seg3] background-color classes per filled segment
  0: ['bg-platinum', 'bg-platinum', 'bg-platinum', 'bg-platinum'],
  1: ['bg-semantic-error', 'bg-platinum', 'bg-platinum', 'bg-platinum'],
  2: ['bg-semantic-warning', 'bg-semantic-warning', 'bg-platinum', 'bg-platinum'],
  3: ['bg-yellow-400', 'bg-yellow-400', 'bg-yellow-400', 'bg-platinum'],
  4: ['bg-semantic-success', 'bg-semantic-success', 'bg-semantic-success', 'bg-semantic-success'],
};

const LABEL: Record<Score, string> = {
  0: '',
  1: 'Faible',
  2: 'Moyen',
  3: 'Fort',
  4: 'Très fort',
};

const LABEL_COLOR: Record<Score, string> = {
  0: 'text-ink-tertiary',
  1: 'text-semantic-error',
  2: 'text-semantic-warning',
  3: 'text-yellow-700',
  4: 'text-semantic-success',
};

export function PasswordStrengthMeter({ password }: Props) {
  const score = useMemo<Score>(() => {
    if (password.length === 0) return 0;
    const result = zxcvbn(password);
    // zxcvbn returns score in [0, 4]. Narrow it.
    return Math.max(0, Math.min(4, result.score)) as Score;
  }, [password]);

  const segments = SEGMENT_FILL[score];

  return (
    <div data-testid="password-strength" aria-live="polite">
      <div className="flex gap-1.5 mt-2">
        {segments.map((cls, i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-colors ${cls}`}
            aria-hidden="true"
          />
        ))}
      </div>
      {score > 0 ? (
        <p className={`mt-1 text-xs font-medium ${LABEL_COLOR[score]}`}>
          Force du mot de passe : {LABEL[score]}
        </p>
      ) : (
        <p className="mt-1 text-xs text-ink-tertiary">Force du mot de passe : —</p>
      )}
    </div>
  );
}
